import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * Whether Lucas's screen is open.
 *
 * The only shared state that exists: the sidebar button lives in the layout and
 * the conversation lives on the home, so one of them has to be able to tell the
 * other. Everything else about the chat — messages, attachments, history — stays
 * on the home, because that is now the only place Lucas is rendered.
 *
 * Deliberately not a shared `useSupportChat()`. There used to be a floating
 * widget calling the hook on its own, which meant two local `messages` arrays
 * over a single server-side conversation. The widget is gone; keeping a shared
 * chat hook "just in case" would recreate the coupling without the second
 * consumer that justified it.
 */

const SupportChatContext = createContext(null);

export function SupportChatProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const value = useMemo(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle],
  );

  return (
    <SupportChatContext.Provider value={value}>
      {children}
    </SupportChatContext.Provider>
  );
}

export function useSupportChatUI() {
  const context = useContext(SupportChatContext);
  if (!context) {
    throw new Error(
      "useSupportChatUI precisa de <SupportChatProvider> acima na árvore",
    );
  }
  return context;
}

export default SupportChatContext;
